import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SingleResponseModel } from '../../shared/models/singleResponse.model';
import {
  CustomerPortalInfo,
  CustomerPortalInvoicePage,
  CustomerPortalLink,
  CustomerPortalSettings,
  CustomerPortalStatusFilter,
} from '../models/customer-portal.models';

/**
 * Gestión del portal por parte del usuario (api/v2/customer-portal): activarlo y crear, regenerar o
 * revocar el enlace de cada contacto. Va por el HttpClient normal, con el token de sesión.
 */
@Injectable({ providedIn: 'root' })
export class CustomerPortalService {
  private readonly baseUrl = `${environment.apiUrl}/api/v2/customer-portal`;

  constructor(private http: HttpClient) {}

  getSettings(): Observable<SingleResponseModel<CustomerPortalSettings>> {
    return this.http.get<SingleResponseModel<CustomerPortalSettings>>(`${this.baseUrl}/settings`);
  }

  saveSettings(enabled: boolean): Observable<SingleResponseModel<CustomerPortalSettings>> {
    return this.http.put<SingleResponseModel<CustomerPortalSettings>>(`${this.baseUrl}/settings`, { enabled });
  }

  /** El enlace vivo del contacto; `value` es null si no tiene. */
  getLink(contactUid: string): Observable<SingleResponseModel<CustomerPortalLink | null>> {
    return this.http.get<SingleResponseModel<CustomerPortalLink | null>>(`${this.baseUrl}/contacts/${contactUid}/link`);
  }

  /** Crea el enlace; si ya tenía uno, el anterior deja de funcionar. */
  createLink(contactUid: string, expiresAt: string | null = null): Observable<SingleResponseModel<CustomerPortalLink>> {
    return this.http.post<SingleResponseModel<CustomerPortalLink>>(`${this.baseUrl}/contacts/${contactUid}/link`, { expiresAt });
  }

  revokeLink(contactUid: string): Observable<SingleResponseModel<boolean>> {
    return this.http.delete<SingleResponseModel<boolean>>(`${this.baseUrl}/contacts/${contactUid}/link`);
  }

  /** URL pública del portal, con el dominio desde el que se usa la webapp. */
  publicUrl(token: string): string {
    return `${window.location.origin}/portal/${token}`;
  }
}

/**
 * Lo que ve el cliente (api/v2/portal/{token}). Usa HttpBackend para saltarse los interceptores: el
 * portal no tiene sesión y no debe mandar el token de quien tenga Handly abierto en ese navegador, ni
 * redirigir al login ante un error.
 */
@Injectable({ providedIn: 'root' })
export class CustomerPortalPublicService {
  private readonly http: HttpClient;

  constructor(backend: HttpBackend) {
    this.http = new HttpClient(backend);
  }

  getInfo(token: string): Observable<SingleResponseModel<CustomerPortalInfo>> {
    return this.http.get<SingleResponseModel<CustomerPortalInfo>>(this.url(token));
  }

  getInvoices(token: string, status: CustomerPortalStatusFilter, year: number | null, page: number, pageSize: number):
    Observable<SingleResponseModel<CustomerPortalInvoicePage>> {
    let params = new HttpParams()
      .set('status', status)
      .set('page', page)
      .set('pageSize', pageSize);

    if (year) {
      params = params.set('year', year);
    }

    return this.http.get<SingleResponseModel<CustomerPortalInvoicePage>>(`${this.url(token)}/invoices`, { params });
  }

  /** Enlace directo al PDF: abrirlo en otra pestaña o, con download, descargarlo. */
  pdfUrl(token: string, invoiceUid: string, download = false): string {
    return `${this.url(token)}/invoices/${invoiceUid}/pdf${download ? '?download=true' : ''}`;
  }

  private url(token: string): string {
    return `${environment.apiUrl}/api/v2/portal/${encodeURIComponent(token)}`;
  }
}
